import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Download, LogIn } from "lucide-react";
import { Link } from "react-router-dom";
import { APP_DOWNLOAD_URL } from "../config";
import { NAV_LINKS } from "../config/nav";
import { useSmoothScroll } from "./SmoothScroll";

export const Logo = ({ compact = false }) => (
  <div className="flex items-center gap-3" data-testid="brand-logo">
    <img
      src="/winter-logo.jpg"
      alt="Winter Protocol logo"
      className={`${compact ? "h-8 w-8" : "h-9 w-9"} rounded-lg object-cover mix-blend-screen`}
    />
    <div className="leading-none">
      <span className="block font-display font-bold tracking-[0.18em] text-sm text-white">
        WINTER PROTOCOL
      </span>
      <span className="mt-1 block font-mono text-[9px] tracking-[0.28em] text-[#99A1AF] uppercase">
        AI-Powered Wealth Ecosystem
      </span>
    </div>
  </div>
);

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const scrollTo = useSmoothScroll();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollTo?.(`#${id}`);
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-[#08080A]/85 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <nav className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 sm:px-8">
        <button onClick={() => go("home")} className="shrink-0" data-testid="nav-home-logo-button" aria-label="Winter Protocol home">
          <Logo />
        </button>

        <div className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.slice(1).map((link) => (
            <button
              key={link.id}
              onClick={() => go(link.id)}
              data-testid={`nav-link-${link.id}`}
              className="text-sm text-[#99A1AF] hover:text-white transition-colors duration-300 tracking-wide"
            >
              {link.label}
            </button>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <Link
            to="/login"
            data-testid="nav-login-link"
            className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/40 px-5 py-2.5 text-sm font-medium text-[#F3E5AB] transition-all duration-300 hover:bg-[#D4AF37]/10 hover:border-[#D4AF37]/70"
          >
            <LogIn className="h-4 w-4" />
            Member Login
          </Link>
          <a
            href={APP_DOWNLOAD_URL}
            download="winter-protocol.apk"
            data-testid="nav-download-app-button"
            className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/40 px-5 py-2.5 text-sm font-medium text-[#F3E5AB] transition-all duration-300 hover:bg-[#D4AF37]/10 hover:border-[#D4AF37]/70"
          >
            <Download className="h-4 w-4" />
            Download App
          </a>
          <button
            onClick={() => go("download")}
            data-testid="nav-get-started-button"
            className="rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-[#08080A] transition-transform duration-300 hover:scale-[1.04]"
          >
            Get Started
          </button>
        </div>

        <button
          className="lg:hidden text-white p-2"
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-menu-toggle"
          aria-label="Toggle navigation menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden border-b border-white/10 bg-[#0B0B0F]/95 backdrop-blur-xl"
            data-testid="mobile-nav"
          >
            <div className="flex flex-col gap-1 px-5 py-6">
              {NAV_LINKS.map((link) => (
                <button
                  key={link.id}
                  onClick={() => go(link.id)}
                  data-testid={`mobile-nav-link-${link.id}`}
                  className="rounded-lg px-3 py-3 text-left text-base text-[#C9CFD9] hover:bg-white/5 hover:text-white transition-colors"
                >
                  {link.label}
                </button>
              ))}
              <div className="mt-4 flex flex-col gap-3">
                <Link
                  to="/login"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-nav-login-link"
                  className="inline-flex items-center justify-center rounded-full border border-white/20 px-5 py-3 text-sm font-medium text-white"
                >
                  Members Login
                </Link>
                <a
                  href={APP_DOWNLOAD_URL}
            download="winter-protocol.apk"
                  data-testid="mobile-nav-download-app-button"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-[#D4AF37]/40 px-5 py-3 text-sm font-medium text-[#F3E5AB]"
                >
                  <Download className="h-4 w-4" />
                  Download App
                </a>
                <button
                  onClick={() => go("download")}
                  data-testid="mobile-nav-get-started-button"
                  className="rounded-full bg-white px-5 py-3 text-sm font-semibold text-[#08080A]"
                >
                  Get Started
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
